import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface FormSkeletonProps {
  /** How many field rows (label + control) to draw. Default 5. */
  rows?: number;
  /** Draw a trailing button-shaped block (submit/save). Default true. */
  action?: boolean;
  /** Drop the outer card chrome (border + radius + padding). */
  bare?: boolean;
  className?: string;
}

/**
 * FormSkeleton — a loading placeholder for settings forms and detail panels
 * (anything that's a stack of labelled fields rather than a table). Each row
 * is a short label bar above a wider control bar, optionally capped by a
 * button-shaped block, all inside the same rounded card the real forms use.
 *
 * Drop-in for the old `<p>{t('common.loading')}</p>` branch:
 *
 *   if (query.isLoading) return <FormSkeleton rows={4} />;
 *
 * The whole block is one `role="status"` region labelled "loading"; the
 * individual bars are decorative. For tables use TableSkeleton; for a whole
 * shapeless page use PageLoader.
 */
export function FormSkeleton({
  rows = 5,
  action = true,
  bare = false,
  className,
}: FormSkeletonProps) {
  const { t } = useTranslation('common');
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('common.loading')}
      className={cn(
        'flex flex-col gap-6',
        !bare && 'rounded-2xl border border-border bg-background p-6',
        className,
      )}
    >
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex flex-col gap-2">
          <Skeleton className="h-3 w-32 rounded-pill" />
          <Skeleton className="h-10 w-full max-w-md rounded-xl" />
        </div>
      ))}
      {action && <Skeleton className="h-10 w-32 rounded-pill" />}
    </div>
  );
}
